import React from "react";
import { useTranslation } from "react-i18next";
import { Delete, Percentage } from "@dreamhub/designsystem/icons";
import Tabs from "@/components/controls/Tabs";
import { IconButton } from "@/components/ui/icon-button";
import IconContainer from "@/components/ui/icon-container";
import Input from "@/components/ui/input";
import Text from "@/components/ui/text";
import { getSymbolAdornmentPadding } from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";
import { BundleDiscountError } from "../bundleUtils";

const TRANSLATION_KEY = "settings.product.bundleDrawer.discount";
const INPUT_ID = "bundle-drawer-discount";

type DiscountType = "percentage" | "amount";

interface BundleDiscountFieldProps {
  value: string;
  isPercentage: boolean;
  error: BundleDiscountError | null;
  onValueChange: (value: string) => void;
  /** The box holds text a number field reports as empty ("-", "1e"). */
  onUnparseableChange: (isUnparseable: boolean) => void;
  onTypeChange: (isPercentage: boolean) => void;
  onRemove: () => void;
  // An amount's sign; a deal's is its contract currency's.
  currencySymbol?: string;
}

// The deal's discount control clamps what is typed; a bundle's default shows
// the error under the field instead, so the admin sees why it won't save.
export const BundleDiscountField: React.FC<BundleDiscountFieldProps> = ({
  value,
  isPercentage,
  error,
  onValueChange,
  onUnparseableChange,
  onTypeChange,
  onRemove,
  currencySymbol
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-1" data-test="bundle-drawer-discount">
      <label htmlFor={INPUT_ID} className="flex min-h-6 items-center pl-1">
        <Text as="span" variant="bodySecondary">
          {t(`${TRANSLATION_KEY}.label`)}
        </Text>
      </label>
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Input
            // A fresh box per type: a reset to "" can't clear text the field
            // already reports as "".
            key={isPercentage ? "percentage" : "amount"}
            id={INPUT_ID}
            type="number"
            inputMode="decimal"
            min={0}
            max={isPercentage ? 100 : undefined}
            step="any"
            value={value}
            onChange={(event) => onValueChange(event.target.value)}
            // Every keystroke, unlike onChange, which skips "-" typed into an
            // empty box because the reported value stays "".
            onInput={(event) =>
              onUnparseableChange(event.currentTarget.validity.badInput)
            }
            placeholder={t(
              `${TRANSLATION_KEY}.${isPercentage ? "percentagePlaceholder" : "amountPlaceholder"}`
            )}
            className="w-full pr-8"
            // Room for a sign wider than "$" (CA$, say) beside the spinner.
            style={
              isPercentage || !currencySymbol
                ? undefined
                : { paddingRight: getSymbolAdornmentPadding(currencySymbol) }
            }
            hasError={Boolean(error)}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? `${INPUT_ID}-error` : undefined}
            data-test="bundle-drawer-discount-input"
          />
          <span
            className="pointer-events-none absolute right-2 top-1/2 flex -translate-y-1/2 items-center"
            aria-hidden="true"
          >
            {isPercentage ? (
              <IconContainer as="span" size="sm" color="tertiary">
                <Percentage />
              </IconContainer>
            ) : (
              <Text as="span" variant="bodyTertiary">
                {currencySymbol || "$"}
              </Text>
            )}
          </span>
        </div>
        <Tabs
          options={{
            percentage: t(`${TRANSLATION_KEY}.percentage`),
            amount: t(`${TRANSLATION_KEY}.amount`)
          }}
          value={isPercentage ? "percentage" : "amount"}
          valueSelected={(type) =>
            onTypeChange((type as DiscountType) === "percentage")
          }
          size="lg"
          dataTest="bundle-drawer-discount-type"
        />
        <IconButton
          variant="iconNoBg"
          size="md"
          onClick={onRemove}
          aria-label={t(`${TRANSLATION_KEY}.remove`)}
          data-test="bundle-drawer-discount-remove"
        >
          <Delete />
        </IconButton>
      </div>
      {error && (
        <Text
          id={`${INPUT_ID}-error`}
          variant="smBodyError"
          className="pl-1"
          data-test="bundle-drawer-discount-error"
        >
          {t(`${TRANSLATION_KEY}.errors.${error}`)}
        </Text>
      )}
    </div>
  );
};
